/**
 * CUSTOMER-R4.9 — never a blank handoff body. Does not infer payment success.
 */
import { useEffect, useRef, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { BankPaymentIntent } from "./bank-payment-intent-v2.js";
import { markBankLaunchFallback, markBankLaunchRetry } from "./bank-launcher-telemetry-v2.js";

function formatRequisitesCopy(intent: BankPaymentIntent): string {
  return [
    `Сума: ${intent.amount_uah} ₴`,
    `Призначення платежу: ${intent.purpose}`,
    `Отримувач: ${intent.recipient_name}`,
    `РНОКПП: ${intent.recipient_tax_id}`,
    `IBAN: ${intent.recipient_iban}`,
  ].join("\n");
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = value;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const copied = document.execCommand("copy");
      area.remove();
      return copied;
    } catch {
      return false;
    }
  }
}

export function PaymentLaunchBridgeV1({
  lang,
  open,
  intent,
  stalled,
  onRetry,
  onCopyRequisites,
  onBackToOrder,
  paymentConfirmed,
}: {
  lang: StoreV2Lang;
  open: boolean;
  intent: BankPaymentIntent;
  stalled: boolean;
  onRetry: () => void;
  onCopyRequisites?: () => void;
  onBackToOrder?: () => void;
  paymentConfirmed?: boolean;
}) {
  const painted = useRef(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  useEffect(() => {
    if (open) painted.current = true;
  }, [open]);

  if (!open) return null;

  const en = lang === "en";

  const copyRequisites = () => {
    markBankLaunchFallback("show_details");
    if (onCopyRequisites) {
      onCopyRequisites();
      return;
    }
    void copyText(formatRequisitesCopy(intent)).then((ok) => {
      if (!ok) return;
      try {
        (window as Window & { Telegram?: { WebApp?: { HapticFeedback?: { notificationOccurred?: (t: string) => void } } } }).Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.(
          "success",
        );
      } catch {
        /* ignore */
      }
      setCopyToast(en ? "Payment details copied" : "Реквізити скопійовано");
      window.setTimeout(() => setCopyToast(null), 2200);
    });
  };

  if (paymentConfirmed) {
    return (
      <div className="sv2-bank-bridge" data-testid="sv2-bank-launch-bridge">
        <div className="sv2-bank-bridge-card">
          {onBackToOrder ? (
            <button
              type="button"
              className="sv2-bank-bridge-back"
              data-testid="sv2-bank-bridge-back"
              onClick={onBackToOrder}
            >
              {en ? "← Back" : "← Назад"}
            </button>
          ) : null}
          <div className="sv2-bank-bridge-icon" aria-hidden="true">
            ✅
          </div>
          <h2>{en ? "Payment confirmed" : "Оплату підтверджено"}</h2>
          <p className="sv2-bank-bridge-order">
            {en ? "Order" : "Замовлення"} {intent.public_order_number}
          </p>
          <p className="sv2-bank-bridge-amount">{intent.amount_uah} ₴</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sv2-bank-bridge" data-testid="sv2-bank-launch-bridge">
      <div className="sv2-bank-bridge-card">
        {onBackToOrder ? (
          <button
            type="button"
            className="sv2-bank-bridge-back"
            data-testid="sv2-bank-bridge-back"
            onClick={onBackToOrder}
          >
            {en ? "← Back" : "← Назад"}
          </button>
        ) : null}
        <div className="sv2-bank-bridge-icon" aria-hidden="true">
          🏦
        </div>
        <h2>{en ? "Opening the bank app" : "Відкриваємо застосунок банку"}</h2>
        <p className="sv2-bank-bridge-order">
          {en ? "Order" : "Замовлення"} {intent.public_order_number}
        </p>
        <p className="sv2-bank-bridge-amount">{intent.amount_uah} ₴</p>
        {stalled ? null : <div className="sv2-bank-bridge-spinner" aria-hidden="true" />}
        <p className="sv2-bank-bridge-hint">{en ? "Payment details are ready." : "Реквізити вже підготовлені."}</p>
        {stalled ? (
          <p className="sv2-bank-bridge-pending" data-testid="sv2-bank-bridge-pending">
            {en ? "Payment not confirmed yet" : "Оплату ще не підтверджено"}
          </p>
        ) : null}
        {stalled ? (
          <div className="sv2-bank-bridge-fallback" data-testid="sv2-bank-bridge-fallback">
            <p data-testid="sv2-bank-handoff-failed">
              {en
                ? "Could not open the bank app automatically"
                : "Не вдалося автоматично відкрити застосунок банку"}
            </p>
            <button
              type="button"
              className="sv2-cta sv2-primary-commerce"
              data-testid="sv2-bank-open-app"
              onClick={() => {
                markBankLaunchRetry();
                onRetry();
              }}
            >
              {en ? "Open bank app" : "Відкрити застосунок банку"}
            </button>
            <button type="button" className="sv2-cta sv2-secondary" onClick={copyRequisites}>
              {en ? "Copy payment details" : "Скопіювати реквізити"}
            </button>
            {copyToast ? (
              <p className="sv2-bank-bridge-copy-toast" data-testid="sv2-bank-bridge-copy-toast" role="status">
                {copyToast}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
